<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col cols="12" md="4">
        <a ref="wizardTop" />
        <template v-for="(step, index) in enabledSteps">
          <div :key="step.order" class="step">
            <h2
              v-if="isStepCurrent(step)"
              :class="{ selected: isStepCurrent(step) }"
            >
              {{ index + 1 }}. {{ step.title }}
            </h2>
            <h3
              v-else
              :class="{ 'bm-wizard-clickable': true }"
              @click="() => goToStep(step)"
            >
              {{ index + 1 }}. {{ step.title }}
            </h3>
            <div v-show="isStepCurrent(step)">{{ step.description }}</div>
          </div>
        </template>
      </v-col>
      <v-col cols="12" md="8">
        <slot />
      </v-col>
    </v-row>
    <bm-wizard-footer
      :registry="registry"
      :finish-button-text="finishButtonText"
      @next="nextStep"
      @previous="previousStep"
      @close="closeWizard"
      @finish="finished"
    />
  </v-container>
</template>

<script setup lang="ts">
import { getScrollParent } from "@/app/ui/utilities";
import BmWizardFooter from "@/components/controls/wizard/BmWizardFooter.vue";
import messages from "@/plugins/i18n";
import { useRouter } from "@/router/router";
import { computed, getCurrentInstance, ref, watch } from "vue";
import type { RawLocation } from "vue-router";
import type { WizardRegistry, WizardStepHandle } from "./useWizardRegistry";

interface BmWizardProps {
  registry: WizardRegistry;
  finishButtonText?: string;
  routeOnClose?: RawLocation;
}

const props = withDefaults(defineProps<BmWizardProps>(), {
  finishButtonText: messages.finish,
});

const emit = defineEmits<{
  (event: "update:current-step", step: number): void;
  (event: "change", step: number): void;
  (event: "submit"): void;
}>();

const { router } = useRouter();
const wizardTop = ref<HTMLElement | null>(null);

const currentStepIndex = computed(() => props.registry.currentStepIndex);
const steps = computed(() => props.registry.steps.value);
const enabledSteps = computed(() => steps.value.filter((step) => step.enabled));

function isStepCurrent(step: WizardStepHandle): boolean {
  return steps.value[currentStepIndex.value] === step;
}

function goToFirstStep() {
  const first = steps.value.find((step) => step.enabled);
  if (first) {
    goToStep(first);
  }
}

function goToStep(newStep: WizardStepHandle) {
  const currentStep = steps.value[currentStepIndex.value];
  const newIndex = steps.value.findIndex((step) => step === newStep);

  if (
    newIndex > currentStepIndex.value &&
    currentStep &&
    !currentStep.validate()
  ) {
    return;
  }

  props.registry.updateStep(newIndex);
  emit("update:current-step", newIndex);
}

function nextStep() {
  const next = steps.value
    .slice(currentStepIndex.value + 1)
    .find((step) => step.enabled);
  if (next) {
    goToStep(next);
  }
}

function previousStep() {
  const prev = steps.value
    .slice(0, currentStepIndex.value)
    .reverse()
    .find((step) => step.enabled);
  if (prev) {
    goToStep(prev);
  }
}

function closeWizard() {
  emit("change", currentStepIndex.value + 1);

  if (props.routeOnClose) {
    router.push(props.routeOnClose);
  } else {
    router.backWithFallback();
  }
}

function finished() {
  for (const step of enabledSteps.value) {
    if (!step.validate()) {
      goToStep(step);
      return;
    }
  }

  emit("submit");
}

function currentStepIndexWatch() {
  if (wizardTop.value !== null) {
    const top = wizardTop.value;
    const instance = getCurrentInstance();

    instance?.proxy?.$vuetify
      ?.goTo(top, {
        offset: 100,
        container: getScrollParent(top, false),
      })
      .then();
  }
}

function onStepsChanged() {
  const current = steps.value[currentStepIndex.value];
  const first = steps.value.find((step) => step.enabled);

  if (!current?.enabled && first) {
    goToStep(first);
  }
}

watch(currentStepIndex, currentStepIndexWatch);
watch(enabledSteps, onStepsChanged, { immediate: true });
</script>

<style scoped lang="sass">
@import "src/sass/colours"

.bm-wizard-clickable
  color: $bm-color-brand02
  cursor: pointer

.selected
  color: $bm-color-brand03

.step
  margin-bottom: 30px
</style>
